import { Injectable } from '@angular/core';
import { Compbaser } from 'app/components/compbaser';
import { ICRUDRecord, Id, IEventBlock, IScheduleEvent } from '../interfaces';
import { take } from 'rxjs/operators';
import { StoreSelectorsService, LocalStoreService, AppService } from "./";
import { actionAddScheduleItem, actionCRUDRecord, actionRemoveInSceneItem, actionRemoveScheduleEvent, actionUpdateSceneItem, actionUpdateScheduleEvent } from '../store/actions/user.actions';
import { getAllEventsInScenes, isScheduleEventsNull } from "../common";
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';
import * as _ from "lodash";

@Injectable()
export class CommonService extends Compbaser {

    constructor(
        private storeSelector: StoreSelectorsService,
        private localStoreService: LocalStoreService,
        private appService: AppService,
        private toastr: ToastrService,
        private translocoService: TranslocoService,
    ) {
        super();
    }

    ngOnInit() {
        // this.cod(
        //     this.appService.getUploadingFiles().subscribe((v) => {
        //         if (!v || !v[0]) return;
        //         this.createResourceByUploading(v[0]);
        //     })
        // );
    }

    /**
     * Update event value of schedule event when resource or scene is renamed
     **/
    public updateScheduleEventValue(event_source: 'scene' | 'resource', sourceIds: Array<Id>, newName: string) {
        this.cod(
            this.storeSelector.getScheduleEventsBySource(event_source, sourceIds)
                .pipe(take(1))
                .subscribe((i_events: IScheduleEvent[]) => {
                    i_events.forEach((e) => {
                        const eventValue = e.event_value ? JSON.parse(e.event_value) : null;
                        if (!eventValue || eventValue.name == newName) return;

                        eventValue.name = newName;
                        const eventValueFix = isScheduleEventsNull(eventValue);
                        const eventValueString = JSON.stringify(eventValueFix);
                        this.storeSelector.dispatch(actionUpdateScheduleEvent({
                            schedule_id: e.schedule_id,
                            schedule_event_id: e.schedule_event_id,
                            data: [{
                                key: 'event_value',
                                value: eventValueString
                            }]
                        }));
                        this.storeSelector.dispatch(actionCRUDRecord({
                            saveRecord: {
                                studioId: this.localStoreService.getStorageConfig().studioId,
                                command: 'update',
                                primary: 'schedule_event_id',
                                table: 'schedule_events',
                                body: {'event_value': isScheduleEventsNull(eventValueString)},
                                id: e.schedule_event_id
                            }
                        }));
                    });
                })
        );
    }

    /**
     * Remove schedule events when resource or scene is remove
     **/
    public removeScheduleEventsBySources(event_source: 'scene' | 'resource', sourceIds: Array<Id>) {
        this.cod(
            this.storeSelector.getScheduleEventsBySource(event_source, sourceIds)
                .pipe(take(1))
                .subscribe((i_events: IScheduleEvent[]) => {
                    if (!i_events) return;

                    const removeIds = i_events.map((e) => e.schedule_event_id);
                    this.storeSelector.dispatch(actionRemoveScheduleEvent({
                        schedule_event_ids: removeIds
                    }));

                    this.storeSelector.dispatch(actionCRUDRecord({
                        saveRecord: {
                            studioId: this.localStoreService.getStorageConfig().studioId,
                            command: 'delete',
                            primary: 'schedule_event_id',
                            table: 'schedule_events',
                            body: {},
                            id: removeIds
                        }
                    }));
                })
        );
    }

    /**
     * Add new schedule
     **/
    async addSchedule(data) {
        return new Promise<string>((resolve, error) => {
            if (!data) return resolve(null);

            const saveRecord: ICRUDRecord = {
                studioId: this.localStoreService.getStorageConfig().studioId,
                command: 'add',
                primary: 'schedule_id',
                table: 'schedules',
                body: {
                    schedule_name: data.schedule_name,
                    description: data.description
                },
                id: null
            };

            this.appService.crudRecord(saveRecord).then((i_crudReply: ICRUDRecord) => {
                /** important dont update store since server command failed **/
                if (!i_crudReply.serverReply.status) return;
                const newScheduleId = i_crudReply.serverReply.id;

                this.storeSelector.dispatch(actionAddScheduleItem({
                    data: {
                        schedule_id: i_crudReply.serverReply.id,
                        schedule_name: data.schedule_name,
                        description: data.description,
                        active: true
                    }
                }));

                resolve(newScheduleId);
            }).catch((err) => error(err));
        });
    }

    /**
     * Remove events used in scenes
     *  1. traverse all scenes to get event blocks by comparing eventName
     *  2. group the event blocks by scene id
     *  3. for each scene, remove EventNames and eventCommands
     */
    removeEventsUsedInScenes(eventName) {
        this.cod(
            this.storeSelector.getScenes().pipe(take(1)).subscribe((i_scenes) => {
                const eventBlocks = getAllEventsInScenes(i_scenes, eventName);
                if (!eventBlocks || eventBlocks.length === 0) {
                    return;
                }

                // group by eventBlocks by scene id
                const eventBlocksGroupBySceneId = _.groupBy(eventBlocks, _block => _block.eventSceneId);

                // remove eventNames and eventCommand for each scene
                Object.keys(eventBlocksGroupBySceneId).forEach((sceneId) => {
                    const blocks = eventBlocksGroupBySceneId[sceneId];
                    const eventNameBlocks = blocks.filter((_block) => _block.eventType.indexOf("EventCommand") === -1);
                    const eventCommandBlocks = blocks.filter((_block) => _block.eventType.indexOf("EventCommand") > -1);

                    // remove EventNames in scenes
                    if (eventNameBlocks.length > 0) {
                        eventNameBlocks.forEach((_block: IEventBlock) => {
                            this.removeEventNameInSceneItem(_block, false);
                        });
                        this.appService.updateMultiScene([sceneId]);
                    }

                    // remove EventCommands in scenes
                    if (eventCommandBlocks.length > 0) {
                        this.removeEventCommandsInScene(sceneId, eventCommandBlocks.map((_block) => _block.eventBlockId), eventName, false);
                    }
                });
            })
        );
    }

    /**
     * Remove EventName used in scene item
     */
    removeEventNameInSceneItem(eventBlock: IEventBlock, isUpdateScene = true) {
        let updateValues = [];
        switch (eventBlock.eventType) {
            case 'selectItemEventName': {
                updateValues.push(
                    {
                        key: "selectItemEventName",
                        value: "",
                        storePath: "$"
                    },
                    {
                        key: "selectItemEventParam",
                        value: "",
                        storePath: "$"
                    },
                );
                break;
            }
            case 'clickEventName': {
                updateValues.push(
                    {
                        key: "clickEventName",
                        value: "",
                        storePath: "$"
                    },
                    {
                        key: "clickEventParam",
                        value: "",
                        storePath: "$"
                    },
                );
                break;
            }
            case 'resultEventName': {
                updateValues.push(
                    {
                        key: "resultEventName",
                        value: "",
                        storePath: "Data[0].DataProvider[0].$"
                    },
                    {
                        key: "resultEventParam",
                        value: "",
                        storePath: "Data[0].DataProvider[0].$"
                    },
                );
                break;
            }
            case 'eventName': {
                updateValues.push(
                    {
                        key: "eventName",
                        value: "",
                        storePath: "Data[0].DataProvider[0].$"
                    },
                );
                break;
            }
            case 'EventCommand': {
                break;
            }
        }

        // update store and database
        if (updateValues.length > 0) {
            this.storeSelector.dispatch(actionUpdateSceneItem({
                sceneId: eventBlock.eventSceneId,
                sceneItemId: eventBlock.eventBlockId,
                values: updateValues,
                disableCRUD: true
            }));
            if (isUpdateScene) {
                this.appService.updateMultiScene([eventBlock.eventSceneId]);
            } else {
                return eventBlock.eventSceneId;
            }
        }
    }

    /**
     * Remove EventCommand used in scene blocks
     * If block is scene loader and no event commands, add default "loader" command
     */
    removeEventCommandsInScene(sceneId: Id, blockIds: Array<Id>, eventName: string, onlyFirstMatching = true) {
        this.cod(
            this.storeSelector.listenPlayersOfScene(sceneId).pipe(take(1)).subscribe((i_players) => {

                // find all event scene blocks with blockIds
                const scenePlayers = i_players.filter((_player) => blockIds.includes(_player.$.id));
                if (!scenePlayers && scenePlayers.length === 0) return;

                let isSceneUpdated = false;
                scenePlayers.forEach((_player) => {
                    const eventCommands = _player.Data["0"].EventCommands?.["0"]?.EventCommand;
                    const blockId = _player.$.id;

                    if (eventCommands) {
                        let updatedEventCommands = [];

                        if (onlyFirstMatching) {
                            // find first matching and remove it if exists
                            const ind = eventCommands.findIndex((e) => e.$.from === eventName);
                            if (ind === -1) return;
                            updatedEventCommands = eventCommands.filter((e, index) => index !== ind);
                        } else {
                            updatedEventCommands = eventCommands.filter((e, index) => e.$.from !== eventName);
                        }

                        // if block is scene loader and no event commands, add default "loader" command
                        if (updatedEventCommands.length === 0 && _player.$.player === this.storeSelector.blockLabels.LOADER.id) {
                            updatedEventCommands = [
                                {
                                    "$": {
                                        "from": "loader",
                                        "command": "load"
                                    }
                                }
                            ];
                        }

                        // update EventCommand store
                        if (updatedEventCommands.length > 0) {
                            this.storeSelector.dispatch(actionUpdateSceneItem({
                                sceneId,
                                sceneItemId: blockId,
                                values: [{
                                    "key": "EventCommand",
                                    "value": updatedEventCommands,
                                    "storePath": "Data[0].EventCommands[0]"
                                }],
                                disableCRUD: true
                            }));

                        } else { // if there is no any event command, remove EventCommands
                            this.storeSelector.dispatch(actionRemoveInSceneItem({
                                sceneId,
                                sceneItemId: blockId,
                                values: [{
                                    "key": "EventCommands",
                                    "value": null,
                                    "storePath": "Data[0]"
                                }],
                                disableCRUD: true
                            }));
                        }
                        isSceneUpdated = true;
                    }
                });

                if (isSceneUpdated) {
                    this.appService.updateMultiScene([sceneId]);
                }
            })
        );
    }
}
